<template>
  <b-container class="job-question-list">
    <b-row>
      <b-col>
        <div class="page-header">Job Question</div>
        <b-button
          class="float-right"
          variant="primary"
          @click="createJobQuestion"
        >
          Create Job Question
        </b-button>
      </b-col>
    </b-row>

    <v-wait :for="loaderId">
      <template slot="waiting">
        <HalfCircleSpinner />
        <div class="spinner-message my-3 text-center">
          Loading the job questions...
        </div>
      </template>
      <DataTable
        ref="dataTable"
        :show-filters="true"
        :show-pager="true"
        :items="filteredQuestions"
        :filters="filters"
        :filter-title="'Filter Job Questions'"
        :fields="fields"
        :no-local-sorting="false"
      >
        <!-- template for filters -->

        <template slot="filters">
          <b-row>
            <b-col class="m-3">
              <label>Prompt</label>
              <b-form-input v-model="filters.prompt.value" />
            </b-col>
          </b-row>
          <b-row>
            <b-col class="m-3">
              <label>Label</label>
              <b-form-input v-model="filters.label.value" />
            </b-col>
          </b-row>
        </template>

        <!-- templates for custom columns -->

        <template slot="type" slot-scope="data">
          {{ data.item.type }}
        </template>

        <template slot="prompt" slot-scope="data">
          <strong>{{ data.item.label }}:</strong>
          <br />
          {{ data.item.member_prompt }}
        </template>

        <template slot="actions" slot-scope="data">
          <b-dropdown
            split
            variant="info"
            size="sm"
            text="Edit"
            @click.stop="editJobQuestion(data.item.id)"
          >
            <b-dropdown-item-button
              @click.stop="onDeleteJobQuestion(data.item.id)"
              class="btn-sm"
            >
              Delete
            </b-dropdown-item-button>
          </b-dropdown>
        </template>
      </DataTable>
    </v-wait>

    <job-question-modal-edit-form
      v-if="showModal"
      :initial-job-question="editedQuestion"
      ref="modal"
      @modalCancel="onModalCancel"
      @jobQuestionSaved="onJobQuestionSaved"
    />
  </b-container>
</template>

<script>
import DataTable from '@/components/DataTable';
import { HalfCircleSpinner } from 'epic-spinners';
import Api from '@/providers/Api';
import { CHART_TYPES, JOB_QUESTION_TYPES } from '@/store';
import JobQuestionModalEditForm from '@/components/job/question/JobQuestionModalEditForm';
import Help from '@/providers/Help';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';

export default {
  name: 'JobQuestionList',
  components: {
    DataTable,
    HalfCircleSpinner,
    JobQuestionModalEditForm,
  },
  data: function () {
    return {
      questions: [],
      editedQuestion: null,
      showModal: false,
      filters: {
        prompt: {
          field: 'prompt',
          type: 'string',
          value: null,
        },
        label: {
          field: 'label',
          type: 'string',
          value: null,
        },
      },
      fields: [
        { key: 'id', sortable: true },
        {
          key: 'prompt',
          sortable: true,
          custom: true,
          label: 'Label / Prompt',
        },
        {
          key: 'type',
          sortable: true,
          custom: true,
          label: 'Type',
        },
        {
          key: 'actions',
          sortable: false,
          custom: true,
        },
      ],
    };
  },
  created: function () {
    this.refreshData();
  },
  computed: {
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    filteredQuestions: function () {
      const filteredByPrompt =
        this.filters.prompt && !!this.filters.prompt.value;
      const filteredByLabel = this.filters.label && !!this.filters.label.value;
      if (filteredByPrompt || filteredByLabel) {
        return this.questions.filter((q) => {
          let isMatch = true;
          if (filteredByPrompt) {
            isMatch &=
              q.member_prompt &&
              q.member_prompt
                .toLowerCase()
                .includes(this.filters.prompt.value.toLowerCase());
          }
          if (filteredByLabel) {
            isMatch &=
              q.label &&
              q.label
                .toLowerCase()
                .includes(this.filters.label.value.toLowerCase());
          }
          return isMatch;
        });
      }
      return this.questions;
    },
  },
  methods: {
    refreshData: async function () {
      this.$wait.start(this.loaderId);
      try {
        this.questions = await Api.getAllJobQuestions();
      } catch (e) {
        this.$toasted.error('Upload data error', { duration: 3000 });
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
    createJobQuestion: function () {
      this.openEditModal({
        id: null,
        prompt: '[New Job Question]',
        type: JOB_QUESTION_TYPES.SLIDER,
        jobQuestionVariables: [
          {
            uid: Date.now(),
            order: 1,
            name: 'NEW_VARIABLE',
            input_label: '[New Variable]',
            visible: true,
            response: {},
          },
        ],
        chart: {
          chart_type: CHART_TYPES.HISTOGRAM,
          chart_config: {},
        },
      });
    },
    editJobQuestion: function (id) {
      const question = this.questions.find((q) => q.id === id);
      this.openEditModal(question);
    },
    openEditModal: function (question = null) {
      this.editedQuestion = question;
      this.showModal = true;
    },
    onModalCancel: function () {
      this.editedQuestion = null;
      this.showModal = false;
    },
    onJobQuestionSaved: function (question) {
      const index = this.questions.findIndex((q) => q.id === question.id);
      if (index !== -1) {
        this.$set(this.questions, index, question);
      } else {
        this.$set(this.questions, this.questions.length, question);
      }
    },
    onDeleteJobQuestion: function (id) {
      Help.confirm.show(
        'Permanently delete this job question?',
        'Delete',
        () => {
          this.deleteJobQuestion(id);
        }
      );
    },
    deleteJobQuestion: async function (id) {
      try {
        await Api.deleteJobQuestion(id);
        const index = this.questions.findIndex((q) => q.id === id);
        this.questions.splice(index, 1);
        this.$toasted.success('Job Question deleted', { duration: 3000 });
        this.$refs.dataTable.refresh();
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      }
    },
  },
};
</script>

<style scoped>
.page-header {
  display: inline-block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  line-height: 1.2;
  font-size: 2rem;
  font-family: 'Questrial', arial, helvetica, sans-serif;
}
</style>
